export const PRODUCT_CATEGORIES = ['tees', 'hoodies', 'accessories', 'other'] as const;

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const CATEGORY_LABEL: Record<ProductCategory, string> = {
  tees: 'Tees',
  hoodies: 'Hoodies',
  accessories: 'Accessories',
  other: 'Other',
};

export function isCategory(value: string | null | undefined): value is ProductCategory {
  return Boolean(value) && (PRODUCT_CATEGORIES as readonly string[]).includes(value as string);
}

export type ProductImage = {
  id: string;
  path: string;
  thumbPath: string | null;
  alt: string;
  position: number;
};

export type Product = {
  id: string;
  slug: string;
  name: string;
  description: string;
  price: number;
  compareAtPrice: number | null;
  sizes: string[];
  soldOut: boolean;
  stock: number | null;
  isActive: boolean;
  sortOrder: number;
  category: ProductCategory;
  createdAt: string;
  images: ProductImage[];
};

export type ProductRow = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  price: number;
  compare_at_price: number | null;
  sizes: string[] | null;
  sold_out: boolean;
  stock: number | null;
  is_active: boolean;
  sort_order: number;
  category: string | null;
  created_at: string;
  product_images?: Array<{
    id: string;
    storage_path: string;
    thumb_path: string | null;
    alt: string | null;
    position: number;
  }> | null;
};

export function mapProduct(row: ProductRow): Product {
  const images = (row.product_images ?? [])
    .map((image) => ({
      id: image.id,
      path: image.storage_path,
      thumbPath: image.thumb_path,
      alt: image.alt ?? '',
      position: image.position,
    }))
    .sort((a, b) => a.position - b.position);

  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    description: row.description ?? '',
    price: row.price,
    compareAtPrice: row.compare_at_price,
    sizes: row.sizes ?? [],
    soldOut: row.sold_out,
    stock: row.stock,
    isActive: row.is_active,
    sortOrder: row.sort_order,
    category: isCategory(row.category) ? row.category : 'other',
    createdAt: row.created_at,
    images,
  };
}

/** Sold out when the flag is set, or when tracked stock has run out. */
export function isSoldOut(product: Product) {
  return product.soldOut || (product.stock !== null && product.stock <= 0);
}
